import axios from 'axios'
import { ButtonPrimary } from 'components/Button'
import { AutoColumn } from 'components/Column'
import IntroCard from 'components/IntroCard'
import { API_POOL_BASE_URL } from 'constants/links'
import { useActiveWeb3React } from 'hooks'
import ReferralInputPanel from 'pages/Testnet/ReferralInputPanel'
import React, { useCallback, useContext, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useWalletModalToggle } from 'state/application/hooks'
import styled, { ThemeContext } from 'styled-components'
import { Text } from 'rebass'
import { TYPE } from 'theme'
import { isAddress } from 'utils'
const ContainerStyled = styled.div`
  width: 100%;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 200px;
  @media (min-width: 502px) {
    padding: 10px;
  }

  max-width: 500px;
`

const SubContainer = styled.div<{ bg1: boolean }>`
  background: ${({ theme }) => theme.bg0};
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`

const SubContainerBody = styled.div`
  padding: 20px;
  background: ${({ theme }) => theme.bg0};
`

const ConnectWalletContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  min-height: 25vh;
  background: ${({ theme }) => theme.bg0};
  padding: 8px;
  border-radius: 14px;
  width: 100%;

  div {
    max-width: 300px;
  }
`
const SubContainerHeader = styled.div`
  border-radius: 14px; 
  background: ${({ theme }) => theme.bg1}
  padding: 20px;
`

const Delegator = () => {
  const { account, library } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const theme = useContext(ThemeContext)
  const [loading, setLoading] = useState(false)

  const [inputValue, setInputValue] = useState<string>('')
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [successMsg, setSuccessMsg] = useState<string>('')

  const [linkedAddress, setLinkedAddress] = useState<null | string>(null)

  const fetchUsername = useCallback(async () => {
    if (!account) return

    try {
      setLoading(true)
      const response = await axios.get(`${API_POOL_BASE_URL}/api/hypurrco/get-addy`, {
        params: { address_evm: account },
      })

      if (response.data.success) {
        setLinkedAddress(response.data.address_l1)
      } else {
        setLinkedAddress(null)
      }

      setLoading(false)
    } catch (error) {
      setLinkedAddress(null)
      console.error('Failed to fetch username:', error)
    } finally {
      setLoading(false)
    }
  }, [account, setLoading])

  React.useEffect(() => {
    fetchUsername()
  }, [fetchUsername, account])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim()
    const partialEvmAddressRegex = /^0x?[a-fA-F0-9]*$/

    if (value.length <= 42 && partialEvmAddressRegex.test(value)) {
      setInputValue(value)
    }
  }

  const sign = async () => {
    if (!library || !account) return

    setLoading(true)
    const signer = library.getSigner()
    const timestamp = Math.floor(Date.now() / 1000)
    const message = `Please sign this message to confirm that you are the owner. Timestamp: ${timestamp}`
    setErrorMsg('')
    setSuccessMsg('')

    try {
      const signedMessage = await signer.signMessage(message)

      const response = await axios.post(`${API_POOL_BASE_URL}/api/hypurrco/link`, {
        address_l1: inputValue,
        address_evm: account,
        signature: signedMessage,
        message: message,
      })

      if (response.data?.message?.length > 0) {
        setSuccessMsg(response.data?.message)
        await fetchUsername()
      }

      if (!response.data.success) {
        throw new Error('Failed to submit signature')
      }
    } catch (error: any) {
      setSuccessMsg('')
      setErrorMsg(error?.response?.data?.message ? error?.response?.data?.message : 'An error occurred.')
      console.error('Error signing or sending message:', error)
    } finally {
      setLoading(false)
    }
  }

  const validAddress = isAddress(inputValue)

  return (
    <>
      <Helmet>
        <title>Reward Delegator | HyperSwap Exchange on Hyperliquid</title>
        <meta name="description" content="" />
        <meta name="keywords" content="HyperSwap, points program, DeFi, Hyperliquid blockchain" />
        <meta property="og:title" content="Reward Delegator | HyperSwap Exchange on Hyperliquid" />
        <meta
          property="og:description"
          content="Welcome to the Reward Delegator Tool! Experiment with HyperSwap's features, and explore our points program."
        />
        <meta property="og:url" content={`${window.location.origin}/#/testnet`} />
      </Helmet>
      <ContainerStyled>
        <IntroCard variant={1} Key="Testnet">
          <h1>Reward Delegator</h1>
          <p>
            This tool allows you to automatically link your HypurrCo staker wallet to your designated HyperEVM wallet.
          </p>
        </IntroCard>

        <>
          <SubContainer bg1={false}>
            <SubContainerHeader>
              <h2 style={{ fontSize: '18px', marginTop: '0' }}>Reward Delegator</h2>
              <p style={{ fontSize: '12px', color: '#C3C5CB', marginBottom: 0 }}>
                To link your wallets, first connect your Hyper EVM wallet.
                <br />
                <br />
                Then, enter your staker wallet address to complete the connection.{' '}
              </p>
            </SubContainerHeader>
            <SubContainerBody>
              {account ? (
                <AutoColumn gap={'md'}>
                  <ReferralInputPanel
                    id="delegate-source-wallet"
                    value={account}
                    handleChange={handleChange}
                    disabled={true}
                    error={false}
                    label="Your wallet address"
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    style={{ margin: 'auto' }}
                    fill="none"
                    stroke="#C3C5CB"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-chevrons-down"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M7 7l5 5l5 -5" />
                    <path d="M7 13l5 5l5 -5" />
                  </svg>
                  {linkedAddress ? (
                    <>
                      <ReferralInputPanel
                        id="delegate-target-wallet"
                        value={linkedAddress}
                        handleChange={handleChange}
                        error={false}
                        disabled={true}
                        label="Linked wallet address"
                      />
                      {successMsg && successMsg.length > 0 && (
                        <p style={{ color: '#4AFB39', margin: 0, fontSize: '12px' }}>Success: {successMsg}</p>
                      )}
                    </>
                  ) : (
                    <>
                      <ReferralInputPanel
                        id="delegate-target-wallet"
                        value={inputValue}
                        handleChange={handleChange}
                        error={!!errorMsg}
                        disabled={false}
                        maxLength={42}
                        label="Target wallet address"
                        placeholder={'0x0000000000000000000000000000000000000000'}
                      />
                      {!validAddress && inputValue.length > 0 && !errorMsg && (
                        <p style={{ color: '#FF6871', margin: 0, fontSize: '12px' }}>Invalid address.</p>
                      )}
                      {inputValue === account && !errorMsg && (
                        <p style={{ color: '#FF6871', margin: 0, fontSize: '12px' }}>
                          Target address cannot be the same as your account address.
                        </p>
                      )}
                      {errorMsg && <p style={{ color: '#FF6871', margin: 0, fontSize: '12px' }}>Error: {errorMsg}</p>}
                      {successMsg && successMsg.length > 0 && (
                        <p style={{ color: '#4AFB39', margin: 0, fontSize: '12px' }}>Success: {successMsg}</p>
                      )}

                      <ButtonPrimary
                        id="join-pool-button"
                        style={{ padding: 16 }}
                        as={'button'}
                        onClick={sign}
                        disabled={loading || inputValue.length === 0 || !validAddress}
                      >
                        <Text
                          fontWeight={400}
                          fontSize={14}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          {loading ? 'Signing...' : 'Sign'}
                        </Text>
                      </ButtonPrimary>
                    </>
                  )}
                </AutoColumn>
              ) : (
                <ConnectWalletContainer>
                  <div>
                    <TYPE.mediumHeader textAlign="center" color={theme.text3}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-user"
                        style={{ marginBottom: '.5rem' }}
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                        <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                      </svg>

                      <div>{'Connect your wallet to access this tool.'}</div>
                    </TYPE.mediumHeader>
                    <ButtonPrimary style={{ marginTop: '2em', padding: '8px 16px' }} onClick={toggleWalletModal}>
                      Connect a wallet
                    </ButtonPrimary>
                  </div>
                </ConnectWalletContainer>
              )}
            </SubContainerBody>
          </SubContainer>
        </>
      </ContainerStyled>
    </>
  )
}
export default Delegator
